<div class="login-form-container">
	<!-- ── LDAP / SAML recognised — no account yet ───────────────────────── -->
	<div
		*ngIf="showLdapPanel"
		class="ldap-recognized-panel"
		data-cy="ldap-recognized-panel"
	>
		<div class="ldap-recognized-panel__icon-ring">
			<i class="mdi mdi-shield-check"></i>
		</div>
		<h4 class="ldap-recognized-panel__title">
			{{ "LDAP_RECOGNIZED_TITLE" | translate }}
		</h4>
		<p class="ldap-recognized-panel__message">
			{{ "LDAP_RECOGNIZED_MESSAGE" | translate }}
		</p>
		<div class="ldap-recognized-panel__actions">
			<button
				class="btn btn--primary btn--lg ldap-recognized-panel__btn-request"
				(click)="requestAccount()"
				data-cy="ldap-request-account-btn"
			>
				<i class="mdi mdi-arrow-right-bold-circle-outline"></i>
				{{ "REQUEST_ACCOUNT" | translate }}
			</button>
			<button
				class="btn ldap-recognized-panel__btn-cancel"
				(click)="cancelLdapRequest()"
				data-cy="ldap-cancel-btn"
			>
				{{ "CANCEL" | translate | capitalize }}
			</button>
		</div>
	</div>

	<!-- ── Normal login form (hidden while the LDAP panel is visible) ──────── -->
	<ng-container *ngIf="!showLdapPanel">
		<!-- ── Loading the server-described form: show a loader instead of the
		     empty single-field form that looks "hung" (COH-3297) ──────────── -->
		<div
			*ngIf="isLoadingForm"
			class="login-form-loading flex flex-column flex-align-items-center flex-justify-content-center full-width"
			data-cy="login-form-loading"
		>
			<div class="loader loader--xl"></div>
		</div>

		<!-- ── Form failed to load after retries: offer a manual retry ─────── -->
		<div
			*ngIf="!isLoadingForm && formLoadFailed"
			class="login-form-error flex flex-column flex-align-items-center flex-justify-content-center full-width"
			data-cy="login-form-error"
		>
			<i class="mdi mdi-alert-circle-outline login-form-error__icon"></i>
			<p class="login-form-error__message">
				{{ "MSG_LOGIN_FORM_LOAD_FAILED" | translate }}
			</p>
			<button
				class="btn btn--primary btn--lg"
				(click)="retryLoadLoginForm()"
				data-cy="login-form-retry-btn"
			>
				<i class="mdi mdi-refresh"></i>
				{{ "RETRY" | translate | capitalize }}
			</button>
		</div>

		<!-- ── Server-described form ready ──────────────────────────────────── -->
		<ng-container *ngIf="!isLoadingForm && !formLoadFailed">
		<form
			id="form"
			enctype="application/x-www-form-urlencoded"
			(keyup.enter)="useGoogleCaptcha ? submitWithCaptcha() : login()"
		>
			<div class="form-group" *ngIf="showUsernameField">
				<i class="input-icon mdi mdi-account"></i>
				<input
					class="form-control"
					type="text"
					name="username"
					placeholder="{{ 'USERNAME' | translate | capitalize }}"
					(keydown)="resetErrorState()"
					[(ngModel)]="loginModel.username"
					required=""
				/>
			</div>
			<div class="form-group" *ngIf="loginModel?.requiresPassword">
				<i class="input-icon mdi mdi-key-variant"></i>
				<input
					class="form-control"
					type="password"
					name="password"
					placeholder="{{ 'PASSWORD' | translate | capitalize }}"
					(keydown)="resetErrorState()"
					[(ngModel)]="loginModel.password"
					required=""
				/>
			</div>
			<div class="form-group" *ngIf="useGoogleCaptcha">
				<div class="mb-1 g-recaptcha-container" [innerHtml]="captchaHtml"></div>
			</div>
			<div [matTooltip]="tooltipText" (mouseenter)="setTooltipText()">
				<!-- changing the buttons for the new google invisible captcha V2 -->
				<ng-container *ngIf="useGoogleCaptcha">
					<!-- Drives the invisible captcha via grecaptcha.execute() (reset-safe)
					     instead of grecaptcha's data-bind click binding, which goes dead
					     after a reset on a failed login (COH-3297). -->
					<button
						id="form-submit"
						class="btn btn--primary btn--lg"
						[csLoader]="$inProgress | async"
						[loaderAdditionalClasses]="['loader--white']"
						[disabled]="
							(showUsernameField && loginModel?.username?.length < 1) ||
							(loginModel?.requiresPassword && loginModel?.password?.length < 1)
						"
						(click)="submitWithCaptcha()"
					>
						{{ "LOGIN" | translate | capitalize }}
					</button>
				</ng-container>
				<ng-container *ngIf="!useGoogleCaptcha">
					<button
						class="btn btn--primary btn--lg"
						[csLoader]="$inProgress | async"
						[loaderAdditionalClasses]="['loader--white']"
						[disabled]="
							(showUsernameField && loginModel?.username?.length < 1) ||
							(loginModel?.requiresPassword && loginModel?.password?.length < 1)
						"
						(click)="login()"
					>
						{{ "LOGIN" | translate | capitalize }}
					</button>
				</ng-container>
			</div>
			<div *ngIf="filterActive(loginOptions).length > 0">
				<div class="mb-3 mt-3 f-body-s f-color-dimmed">{{ "OR" | translate }}</div>
				<ng-container *ngFor="let option of filterActive(loginOptions)">
					<div [@inOutAnimation]>
						<button
							class="btn btn--icon-and-text btn--lg auth-provider-button text-truncate text-no-whitespace"
							[disabled]="$inProgress | async"
							(click)="switchMethod(option)"
						>
							<i class="{{ getAuthIcon(option.key) }}"></i>
							<div class="full-width">
								{{ "CONTINUE_WITH" | translate: { method: option.label } }}
							</div>
						</button>
					</div>
				</ng-container>
			</div>
		</form>
		<p class="card-text login-footer-text">
			{{ "IF_YOU_HAVE_PROBLEMS_LOGGING_IN" | translate | capitalize }}
			<a href="mailto:{{ loginConfig.supportEmail }}" class="link">{{
				loginConfig.supportEmail
			}}</a>
		</p>
		</ng-container>
	</ng-container>

	<!-- ── Bottom bar — lives outside *ngIf so it can fade out smoothly ──── -->
	<div
		class="login-form--bottom-bar"
		[class.login-form--bottom-bar--center]="
			!showRequestAccess || !showResetPassword
		"
		[class.login-form--bottom-bar--hidden]="showLdapPanel"
	>
		<a
			[routerLink]="loginConfig.RECOVER_URL"
			*ngIf="showResetPassword"
			class="link font-weight-bold"
		>
			{{ "FORGOT_PASSWORD" | translate | capitalize }}
		</a>
		<a
			[routerLink]="loginConfig.REQUEST_ACCESS_URL"
			*ngIf="showRequestAccess"
			class="link font-weight-bold"
			data-cy="request-access-btn"
		>
			{{ "REQUEST_ACCESS" | translate | capitalize }}
		</a>
	</div>
</div>
